import { useCallback, useEffect, useState } from 'react';
import { ContentResponse, StatusResponse } from './type';
import { acknowledge, getContent, getStatus } from './api';
import { useLocation } from 'react-router-dom';

interface TOSStatus extends StatusResponse {
  display: boolean;
  latestDeclined: string | undefined;
}

export const useTOSStatus = (routes: string[], accessToken: string) => {
  const location = useLocation();

  const [status, setStatus] = useState<TOSStatus>({
    display: false,
    hasAcknowledged: true,
    acknowledgedVersion: undefined,
    latestVersion: undefined,
    latestDeclined: '',
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [content, setContent] = useState<ContentResponse>();

  const fetchTOSStatus = async () => {
    try {
      const response = await getStatus(accessToken);
      setStatus((status) => ({
        ...status,
        ...response,
      }));
      return response;
    } catch (err) {
      setError('Failed to fetch TOS status');
    } finally {
      setIsLoading(false);
    }
  };

  const acknowledgeTOS = useCallback(async () => {
    setIsLoading(true);
    await acknowledge(content?.version, accessToken);
    const response = await fetchTOSStatus();
    if (response?.hasAcknowledged) {
      setStatus((oldStatus) => ({ ...oldStatus, display: false }));
    }
    // eslint-disable-next-line
  }, [accessToken, content]);

  const declineTOS = useCallback(async () => {
    setStatus((prev) => ({
      ...prev,
      display: false,
      latestDeclined: location.pathname,
    }));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchTOSStatus();
    // eslint-disable-next-line
  }, [accessToken]);

  useEffect(() => {
    getContent().then((response) => setContent(response));
  }, []);

  useEffect(() => {
    if (status.latestVersion === '') return;
    if (
      status.latestDeclined !== '' &&
      location.pathname.includes('/dashboard')
    )
      return;
    const matchedVersions =
      !status.hasAcknowledged ||
      status.latestVersion !== status.acknowledgedVersion;

    if (matchedVersions) {
      const isRouteMatched = routes.some((route) =>
        location.pathname.includes(route)
      );
      if (isRouteMatched && !status.display) {
        setStatus((prev) => ({
          ...prev,
          display: true,
        }));
      }
    }
  }, [location.pathname, routes, status]);

  return { ...status, content, isLoading, error, acknowledgeTOS, declineTOS };
};
